import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography,
    List, ListItemButton, ListItemText, CircularProgress, Alert
} from '@mui/material';
import { Subcontractor, ResourceType } from '../types/subcontractTypes';
import { getAvailableSubcontractors } from '../api/subcontractApi';
import { formatRUT } from '../utils/validation';

interface SubcontractPickerModalProps {
    open: boolean;
    onClose: () => void;
    /** Confirma el registro con la empresa elegida y las cantidades ingresadas. */
    onSave: (subcontractor: Subcontractor, headcount: number, totalHours: number) => void;
    /** Determina qué campos se piden y cómo se rotulan. */
    resourceType: ResourceType;
    /** Proyecto del reporte diario; filtra las empresas disponibles. */
    projectId: number;
    /** Sección (especialidad) de la actividad; filtra las empresas disponibles. */
    section: string;
    /** Identificadores de empresas ya registradas en este frente y tipo de recurso. */
    alreadyAddedIds: number[];
}

const SubcontractPickerModal: React.FC<SubcontractPickerModalProps> = ({
    open,
    onClose,
    onSave,
    resourceType,
    projectId,
    section,
    alreadyAddedIds
}) => {
    const isPersonnel = resourceType === 'PERSONNEL';

    const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<Subcontractor | null>(null);
    const [headcount, setHeadcount] = useState('');
    const [totalHours, setTotalHours] = useState('');

    useEffect(() => {
        if (!open) return;

        setSearch('');
        setSelected(null);
        setHeadcount('');
        setTotalHours('');
        setLoadError('');

        const fetchAvailable = async () => {
            try {
                setLoading(true);
                // El backend devuelve solo empresas vigentes, operativas, vinculadas a este
                // proyecto, con la especialidad correspondiente a la seccion de la actividad
                // y cuyo alcance de recurso incluya el area desde la que se abrio el modal.
                const data = await getAvailableSubcontractors(projectId, section, resourceType);
                setSubcontractors(data);
            } catch (error) {
                console.error('Error al cargar subcontratistas disponibles', error);
                setLoadError('No se pudo cargar la lista de empresas subcontratistas.');
            } finally {
                setLoading(false);
            }
        };
        fetchAvailable();
    }, [open, projectId, section, resourceType]);

    // Se ocultan las empresas ya registradas en este frente y tipo de recurso, para que la
    // regla de no duplicar no se pueda infringir desde la interfaz.
    const selectable = useMemo(
        () => subcontractors.filter(item => !alreadyAddedIds.includes(item.id)),
        [subcontractors, alreadyAddedIds]
    );

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return selectable;
        return selectable.filter(item =>
            item.businessName.toLowerCase().includes(term) ||
            item.rut.toLowerCase().includes(term) ||
            formatRUT(item.rut).toLowerCase().includes(term)
        );
    }, [selectable, search]);

    const parsedHeadcount = Number(headcount);
    const parsedTotalHours = Number(totalHours);

    // No se permite guardar sin empresa de la lista ni con cantidades vacias o negativas.
    const canSave = !!selected
        && headcount !== '' && !Number.isNaN(parsedHeadcount) && parsedHeadcount > 0
        && totalHours !== '' && !Number.isNaN(parsedTotalHours) && parsedTotalHours > 0;

    const handleSave = () => {
        if (!selected || !canSave) return;
        onSave(selected, parsedHeadcount, parsedTotalHours);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold', color: '#004F86' }}>
                {isPersonnel ? 'Agregar Personal Subcontratado' : 'Agregar Equipos Subcontratados'}
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant="caption" color="textSecondary">
                    Solo se muestran empresas activas asignadas a este proyecto y con la especialidad
                    de la actividad ({section || 'sin sección'}).
                </Typography>

                <TextField
                    label="Buscar por RUT o razón social"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={loading}
                />

                {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
                        {filtered.length === 0 ? (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body2" color="textSecondary">
                                    {selectable.length === 0
                                        ? 'No hay empresas disponibles para este proyecto y especialidad.'
                                        : 'Ninguna empresa coincide con la búsqueda.'}
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {filtered.map(item => (
                                    <ListItemButton
                                        key={item.id}
                                        selected={selected?.id === item.id}
                                        onClick={() => setSelected(item)}
                                    >
                                        <ListItemText
                                            primary={item.businessName}
                                            secondary={formatRUT(item.rut)}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <TextField
                        label={isPersonnel ? 'Dotación (N° Personas)' : 'Cantidad (N° Equipos)'}
                        value={headcount}
                        onChange={(e) => setHeadcount(e.target.value)}
                        type="number"
                        inputProps={{ min: 0, step: 1 }}
                        required
                        fullWidth
                        disabled={!selected}
                    />
                    <TextField
                        label={isPersonnel ? 'Total HH (Horas Hombre)' : 'Total HM (Horas Máquina)'}
                        value={totalHours}
                        onChange={(e) => setTotalHours(e.target.value)}
                        type="number"
                        inputProps={{ min: 0, step: 'any' }}
                        required
                        fullWidth
                        disabled={!selected}
                        helperText="Total de la jornada, no por persona o equipo"
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">Cancelar</Button>
                <Button onClick={handleSave} variant="contained" color="primary" disabled={!canSave}>
                    Agregar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SubcontractPickerModal;
